import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  InputNumber,
  Modal,
  Row,
  Spin,
  Table,
  Tag,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { EyeOutlined, HomeOutlined, SwapOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  InventoryRequestStatus,
  UserType,
} from "../../../configs/constants";
import moment from "moment";
import { AppContext } from "../../../context/AppContextProvider";
import {
  approveStockTransfersRequest,
  cancelRejectStockTransfersRequest,
  getStockTransfers,
} from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { handleApiError } from "../../../utils/errorHandler";

const ViewTransfer = () => {
  const { appConfig, authUser } = useContext(AppContext);
  const [transfersData, setTransfersData] = useState({});
  const [reloadData, setReloadData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState([]);
  const { t } = useTranslation();
  let navigate = useNavigate();
  let { transferId } = useParams();

  useEffect(() => {
    setLoading(true);
    getStockTransfers()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTransfersData(
          response.data.data.stock_transfers.find(
            (transfer) => transfer.id === Number(transferId),
          ),
        );
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoading(false);
      });
  }, [transferId, reloadData, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/inventory/stock-transfer",
      breadcrumbName: t("stock_transfer"),
      icon: <SwapOutlined />,
    },
    {
      path: "view_transfer",
      breadcrumbName: t("view_transfer"),
      icon: <EyeOutlined />,
    },
  ];

  const handlePreAccept = (data) => {
    setModalData(data);
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setModalData([]);
  };

  const handleCancelReject = (id) => {
    const formData = new FormData();
    if (id) {
      formData.append("id", id);
    }
    cancelRejectStockTransfersRequest(formData)
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          // Success
          NotificationWithIcon(
            "success",
            t("transfer_updated"),
            t("transfer_updated_successfully"),
          );
          setReloadData(!reloadData);
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };
  const handleAccept = () => {
    setLoading(true);
    let transactionObj = {
      id: modalData.id,
    };
    transactionObj.transfer_products = modalData.products.map((item) => ({
      transfer_product_id: item.id,
      approved_quantity: item.approved_quantity,
    }));
    // Sending Request to API
    approveStockTransfersRequest(transactionObj)
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          // Success
          NotificationWithIcon(
            "success",
            t("transfer_updated"),
            t("transfer_updated_successfully"),
          );
          setReloadData(!reloadData);
          setModalVisible(false);
          setModalData([]);
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoading(false);
      });
  };

  const tableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("image"),
      width: 85,
      render: (record) => {
        return (
          <>
            <img
              alt={record.product.name_en}
              src={
                record.product.image
                  ? record.product.image
                  : "/images/image-not-found.png"
              }
              className="app-table-thumbnail"
            />
          </>
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          <>
            {appConfig.direction === "ltr"
              ? record.product.name_en
              : record.product.name}
          </>
        );
      },
    },
    {
      title: t("requested_quantity"),
      render: (record) => {
        return <>{record.requested_quantity}</>;
      },
    },
  ];
  const approvedColumn = [
    {
      title: t("approved_quantity"),
      render: (record) => {
        return <>{record.approved_quantity}</>;
      },
    },
  ];

  const adjustmentReducer = (data, quantity) => {
    let updatedAdjustment = JSON.parse(JSON.stringify(modalData));
    let productId = data.product.id;
    let indexFound = updatedAdjustment.products.findIndex(
      (product) => product.product.id === productId,
    );
    updatedAdjustment.products[indexFound].approved_quantity = quantity;
    setModalData(updatedAdjustment);
  };

  const acceptingTableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("image"),
      width: "90px",
      render: (record) => {
        return (
          <>
            <img
              alt={record.product.name_en}
              src={
                record.product.image
                  ? record.product.image
                  : "/images/image-not-found.png"
              }
              className="app-table-thumbnail"
            />
          </>
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          <>
            {appConfig.direction === "ltr"
              ? record.product.name_en
              : record.product.name}
          </>
        );
      },
    },
    {
      title: t("requested_quantity"),
      render: (record) => {
        return <>{record.requested_quantity}</>;
      },
    },
    {
      title: t("approved_quantity"),
      render: (record) => {
        return (
          <>
            <InputNumber
              className="app-w-100px"
              min={0}
              onChange={(value) =>
                record.requested_quantity <= value
                  ? adjustmentReducer(record, record.requested_quantity)
                  : adjustmentReducer(record, value)
              }
              value={record.approved_quantity}
            />
          </>
        );
      },
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("view_transfer")}
          onBack={() => navigate("/inventory/stock-transfer")}
          ghost={false}
          className="app-p-0"
        />
      </Card>
      <Spin spinning={loading}>
        <Card
          style={{
            direction: appConfig.direction
              ? appConfig.direction === "ltr"
                ? "ltr"
                : "rtl"
              : "ltr",
          }}
        >
          {transfersData.id && (
            <>
              <Row gutter={[20, 20]}>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("date")}</div>
                  <div className="app-detail-value">
                    {transfersData.date_time
                      ? moment(transfersData.date_time).format(
                          "MMM. D, YYYY hh:mm a",
                        )
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("created_by")}</div>
                  <div className="app-detail-value">
                    {transfersData.created_by
                      ? transfersData.created_by.name
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("status")}</div>
                  <div className="app-detail-value">
                    {transfersData.status === InventoryRequestStatus.Pending && (
                      <Tag color="orange">{t("pending")}</Tag>
                    )}
                    {transfersData.status === InventoryRequestStatus.Completed && (
                      <Tag color="green">{t("completed")}</Tag>
                    )}
                    {transfersData.status === InventoryRequestStatus.Cancelled && (
                      <Tag color="red">{t("cancelled")}</Tag>
                    )}
                    {transfersData.status === InventoryRequestStatus.Rejected && (
                      <Tag color="red">{t("rejected")}</Tag>
                    )}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("branch_from")}</div>
                  <div className="app-detail-value">
                    {transfersData.branch_from
                      ? transfersData.branch_from.name
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("branch_to")}</div>
                  <div className="app-detail-value">
                    {transfersData.branch_to
                      ? transfersData.branch_to.name
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                  xxl={24}
                >
                  <Table
                    columns={
                      transfersData.status === InventoryRequestStatus.Pending
                        ? [...tableColumns]
                        : [...tableColumns, ...approvedColumn]
                    }
                    dataSource={transfersData.products}
                    rowKey="id"
                    pagination={false}
                  />
                  {transfersData.status === InventoryRequestStatus.Pending &&
                    (authUser.type === UserType.BusinessOwner ||
                      authUser.can_approve_stock_transfer) && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          flexWrap: "wrap",
                          alignItems: "center",
                          padding: "20px 0 0",
                          columnGap: "20px",
                        }}
                      >
                        {transfersData.branch_to.id === authUser.branch_id ? (
                          <>
                            <Button
                              type="text"
                              danger
                              onClick={() =>
                                handleCancelReject(transfersData.id)
                              }
                            >
                              {t("cancel_transfer")}
                            </Button>
                            <Button
                              type="primary"
                              onClick={() => {
                                navigate(
                                  "/inventory/stock-transfer/" +
                                    transfersData.id +
                                    "/edit",
                                  {
                                    state: transfersData,
                                  },
                                );
                              }}
                            >
                              {t("edit_transfer")}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              type="primary"
                              onClick={() => handlePreAccept(transfersData)}
                            >
                              {t("accept_transfer")}
                            </Button>
                            <Button
                              type="text"
                              danger
                              onClick={() =>
                                handleCancelReject(transfersData.id)
                              }
                            >
                              {t("reject_transfer")}
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                </Col>
              </Row>
            </>
          )}
        </Card>
      </Spin>
      <Modal
        open={modalVisible}
        title={t("accept_transfer")}
        onOk={handleAccept}
        onCancel={handleCancel}
        width={1000}
        footer={null}
      >
        <Table
          columns={acceptingTableColumns}
          dataSource={modalData.products}
          rowKey="id"
          pagination={false}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            alignItems: "center",
            padding: "20px 0 0",
            columnGap: "20px",
          }}
        >
          <Button
            type="default"
            key="back"
            loading={loading}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>
          <Button type="primary" loading={loading} onClick={handleAccept}>
            {t("confirm_transfer")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ViewTransfer;
